import { Col, Container, Row } from "react-bootstrap";
import { ArrowUpCircle, BarChart, Calendar, Calendar2Day, Calendar2Heart, ArrowLeftCircle, CalendarRange, CarFront, CurrencyDollar, GearWideConnected, House, IconProps, ListCheck, PCircle, PCircleFill, People, PersonBadge, Shield, ShieldExclamation, Thermometer, Ticket } from "react-bootstrap-icons";
import { NavLink, Outlet, useLocation, useRouteLoaderData, useNavigate } from "react-router-dom";
import { ExtendedFacility } from ".";
import { PageTitle } from "./Controls/PageTitle";
import { OnboardingBanner } from "./Controls/OnboardingBanner";
import { CollapsibleSidebar } from "./Controls/CollapsibleSidebar";
import { CollapsibleMenuItem } from "./Controls/CollapsibleMenuItem";
import { useRef, useEffect } from "react";
import { navigationService } from "./services/NavigationService";
import { useAuthorization } from "./hooks/useAuthorization";

interface MenuHeaderProps {    
    text: string;    
}

export function MenuHeader( props: MenuHeaderProps ) {        
    return <div className={`facility-menu-header`}>        
        <div className="fs-4 d-flex align-items-center justify-content-center gap-1 grid-col-span-5 text-center align-self-center">
            <PageTitle className="fw-normal text-truncate">
                Finance
            </PageTitle>
        </div>
    </div>;
}

export function AppFacility() {
    const loc        = useLocation();
    const nav        = useNavigate();
    const facility   = useRouteLoaderData("facility") as ExtendedFacility;
    const facilityId = facility.facilityId;
    const contentRef = useRef<HTMLDivElement>(null);
    const { canAccessPath } = useAuthorization();

    const can = (tab: 'overview'|'listing'|'status'|'parker'|'plan'|'rate'|'finance'|'reports') => {
        const path = `/facility/${facilityId}/${tab}`;
        return canAccessPath(path);
    };

    // Reset scroll position when location changes
    useEffect(() => {
        if (contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
    }, [loc.pathname]);

    //for handling mobile and desktop versions of these pages
    const [menuClassName, contentClassName] = loc.pathname === ( `/facility/${facilityId}` ) ? ["", "d-none"] : ["d-none", ""];
    const activeOverride = loc.pathname === ( `/facility/${facilityId}` ) ? true : false;

    // Redirect users who try to access a disallowed tab to the first permitted tab
    useEffect(() => {
        // current segment after /facility/:id
        const seg = loc.pathname.replace(`/facility/${facilityId}`, '').split('/').filter(Boolean)[0];
        
        // If no segment (on base facility path), do nothing - landing shows menu
        if (!seg) return;

        // Check if user can access the current path
        if (!canAccessPath(loc.pathname)) {
            // Find first allowed tab
            const tabs: Array<'overview'|'listing'|'status'|'parker'|'plan'|'rate'|'finance'|'reports'> = 
                ['overview','listing','status','parker','plan','rate','finance','reports'];
            
            for (const tab of tabs) {
                if (can(tab)) {
                    nav(`./${tab}`, { replace: true });
                    return;
                }
            }
        }
    }, [loc.pathname, facilityId, canAccessPath, can, nav]);
    return <Container fluid className="fullheight pt-2">
            <OnboardingBanner
                facilityId={facilityId}
                facilityName={facility.name ?? ""}
                show={!facility.account?.isOnboarded}
            />
            <div className="d-flex fullheight">
                <CollapsibleSidebar 
                    className={menuClassName}
                    header={
                        <PageTitle className="fw-normal fs-4 text-truncate">
                            {facility.name}
                        </PageTitle>
                    }
                >
                    <CollapsibleMenuItem special default icon={<ArrowLeftCircle />} to="/operator/facilities" text="Back to Operator" 
                              onClick={() => navigationService.navigateToOperator(nav)} /> 
                    {can('overview') && (
                        <CollapsibleMenuItem active={activeOverride} default icon={<House />} to="./overview" text="Overview" />
                    )}
                    {can('listing') && (
                        <CollapsibleMenuItem icon={<ListCheck      />} to="./listing"      text="Listing"   />
                    )}
                    {can('status') && (
                        <CollapsibleMenuItem icon={<PCircle        />} to="./status"       text="Transient"    />
                    )}
                    {can('parker') && (
                        <CollapsibleMenuItem icon={<People         />} to="./parker"       text="Monthly"   />
                    )}
                    {can('plan') && (
                        <CollapsibleMenuItem icon={<Calendar2Heart />} to="./plan"         text="Plans"     subtext="Monthly" />
                    )}
                    {can('rate') && (
                        <CollapsibleMenuItem icon={<Ticket         />} to="./rate"         text="Rates"     subtext="Transient" />
                    )}
                    {can('finance') && (
                        <CollapsibleMenuItem icon={<CurrencyDollar />} to="./finance/overview"      text="Finance"   />
                    )}
                    {can('reports') && (
                        <CollapsibleMenuItem icon={<BarChart       />} to="./reports"      text="Reports"   />
                    )}
                </CollapsibleSidebar>
                <div
                     ref={contentRef}
                     className={`${contentClassName} p-0 m-0 d-md-grid h-100 scroll-y-auto flex-fill`}
                     style={{ gridTemplateRows: "1fr", overflowX: "hidden" }}>
                    <Outlet />
                </div>
            </div>
    </Container>;
}